/**
 * React Query hooks for CRM pipelines.
 */

import { useQuery } from '@tanstack/react-query';
import api, { CrmPipeline, CrmPipelineSummary } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function usePipelines() {
  const query = useQuery<{ pipelines: CrmPipeline[] }>({
    queryKey: queryKeys.crm.pipelines.all(),
    queryFn: () => api.listCrmPipelines(),
  });

  if (query.isError && !query.isFetching) {
    toast({ title: 'Error', description: 'Failed to load pipelines', variant: 'destructive' });
  }

  return {
    pipelines: query.data?.pipelines ?? [],
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}

export function usePipelineSummary(pipelineId: string | null) {
  const query = useQuery<CrmPipelineSummary>({
    queryKey: queryKeys.crm.deals.pipelineSummary(pipelineId ?? '_none'),
    queryFn: () => api.getCrmPipelineSummary(pipelineId as string),
    enabled: Boolean(pipelineId),
  });

  if (query.isError && !query.isFetching) {
    toast({ title: 'Error', description: 'Failed to load pipeline', variant: 'destructive' });
  }

  return {
    summary: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}
